import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    OnDestroy,
    signal,
    HostBinding
} from '@angular/core';
import { TranslateDirective } from 'i18n';
import { contrastColor, darkenColor } from 'shared';

type CountdownUnit = {
    label: string;
    value: string;
};

@Component({
    selector: 'countdown',
    standalone: true,
    templateUrl: './countdown.component.html',
    styleUrls: ['./countdown.component.scss'],
    imports: [CommonModule, TranslateDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountdownComponent implements OnDestroy {

    @Input({ required: true })
    set target(value: number | Date | string) {
        this.targetTime = this.normalize(value);
        this.restart();
    }

    @Input()
    set color(value: string) {
        if (value) {
            this.backgroundColor = value;
            this.borderColor = darkenColor(value, 15);
        }
    }

    @Input() compact = false;

    @Output() remaining = new EventEmitter<number>();
    @Output() completed = new EventEmitter<void>();

    @HostBinding('style.--backgroundColor') backgroundColor: string = '#111';
    @HostBinding('style.--borderColor') borderColor: string = 'transparent';
    @Input() @HostBinding('style.--foregroundColor') foregroundColor: string = 'inherit';
    
    private targetTime = 0;
    private rafId: number | null = null;
    private lastSecond = -1;
    private completionEmitted = false;
    private startIndex: number | null = null;

    private units$ = signal<CountdownUnit[]>([]);
    units = this.units$.asReadonly();

    private unitsMap: Record<string, CountdownUnit> = {};

    trackByUnitValue = (index: number, unit: CountdownUnit) => {
        return `${index}.${unit.value}`;
    }

    constructor() {

    }

    ngOnDestroy() {
        this.stop();
    }

    private loop = () => {
        const now = Date.now();
        const remaining = Math.max(this.targetTime - now, 0);
        const remainingSeconds = Math.floor(remaining / 1000);

        this.remaining.emit(remainingSeconds);

        if (remainingSeconds !== this.lastSecond) {
            this.lastSecond = remainingSeconds;
            this.update(remainingSeconds);
        }

        if (remainingSeconds > 0) {
            this.rafId = requestAnimationFrame(this.loop);
        } else if (!this.completionEmitted) {
            this.completed.emit();
            this.completionEmitted = true;
            this.stop();
        }
    };

    private stop() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    private restart() {
        this.stop();
        this.lastSecond = -1;
        this.completionEmitted = false;
        this.startIndex = null;
        this.loop();
    }

    private update(totalSeconds: number) {
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const values = [
            { label: 'days', value: days },
            { label: 'hours', value: hours },
            { label: 'minutes', value: minutes },
            { label: 'seconds', value: seconds }
        ];

        if (this.startIndex === null) {
            const firstNonZero = values.findIndex(v => v.value > 0);
            this.startIndex = firstNonZero === -1 ? values.length - 1 : firstNonZero;
        }

        const visible = values.slice(this.startIndex);

        const updated: CountdownUnit[] = [];

        visible.forEach(v => {
            const value = v.value.toString().padStart(2, '0');

            // Only update if changed
            // (alternatively relay purely on trackBy to avoid unneeded DOM manipulations)
            if (!this.unitsMap[v.label] || this.unitsMap[v.label].value !== value) {
                const unit: CountdownUnit = { label: v.label, value };
                this.unitsMap[v.label] = unit;
            }

            updated.push(this.unitsMap[v.label]);
        });

        this.units$.set(updated);
    }


    private normalize(value: number | Date | string): number {
        if (value instanceof Date) return value.getTime();
        if (typeof value === 'string') return new Date(value).getTime();
        return value;
    }
}
